import React, { useMemo, useState } from 'react';
import { BusinessUnit, SavedSearch, Segment } from 'sl-api-connector';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import UserTimestampCell from 'src/components/BeaconV2Components/UserTimestampCell/UserTimestampCell';
import { Link } from 'react-router-dom';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { getAppName } from 'src/utils/app';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { BusinessUnitsCreationModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/BusinessUnitsCreation/BusinessUnitsCreationModal';
import { BusinessUnitsDeletionModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/BusinessUnitsCreation/BusinessUnitsDeletionModal';
import { truncateWithEllipsis } from 'src/utils/stringFormatting';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useStacklineTheme } from '@stackline/ui';

import { NoDataPlaceholderWithChildrenSmall } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/NoDataPlaceholder';
import { CreateNewSegmentLink } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsViewTab';
import { Box } from '@mui/material';
import { EMPTY_BUSINESS_UNIT_NAME } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/utils';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useDorisData } from 'src/utils/Hooks/useDorisData';

interface SegmentData {
  fieldKey: string;
  retailSales: number;
  retailPrice: number;
  unitsSold: number;
}

interface BusinessUnitMetricCellProps {
  fieldName: string;
  segmentId: string;
  segmentData: SegmentData;
  isLoading: boolean;
}
/**
 * Renders a metric cell for the business unit table. It will represent
 * the total metric value for the segment in the table row.
 */
const BusinessUnitMetricCell = ({ fieldName, segmentData, isLoading }: BusinessUnitMetricCellProps) => {
  const formatMetric = useMetricFormatter();
  const field = useMemo(() => {
    return INDEX_FIELDS.getField(getAppName(), 'sales', fieldName);
  }, [fieldName]);

  const values = useMemo(() => {
    if (!segmentData) {
      return null;
    }
    return segmentData[fieldName];
  }, [segmentData, fieldName]);

  return (
    <Text variant="body2">
      {isLoading ? (
        <SlSkeleton height={12} width="100%" />
      ) : (
        formatMetric(values, field.metricType, {
          showFullValue: field.metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2
        })
      )}
    </Text>
  );
};

export interface BusinessUnitTableProps {
  businessUnit: BusinessUnit;
}

/**
 * Displays a table of the segments inside of a business unit
 */
const AtlasV2BusinessUnitTable = ({ businessUnit }: BusinessUnitTableProps) => {
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const { savedSearchesById } = useAppSelector((state) => state.segments);

  const visitedNodes = new Set();
  const segmentEntities = new Set();

  const segmentMapperGenerator = (savedEntity) => {
    if (!savedEntity || !['segment', 'businessunit'].includes(savedEntity?.type)) {
      return;
    }

    if (visitedNodes.has(savedEntity.id)) {
      return;
    }

    visitedNodes.add(savedEntity.id);

    // If the node is a segment, collect the ENTIRE ENTITY
    if (savedEntity.type === 'segment') {
      segmentEntities.add(savedEntity);
      return;
    }

    // If the node is a business unit, process its children
    if (savedEntity.type === 'businessunit' && savedEntity.children?.length > 0) {
      savedEntity.children.forEach((childrenId) => {
        const savedSearchEntity = savedSearchesById.get(childrenId);
        if (savedSearchEntity) {
          segmentMapperGenerator(savedSearchEntity);
        }
      });
    }
  };

  segmentMapperGenerator(businessUnit);

  const segments = Array.from(segmentEntities);
  const theme = useStacklineTheme();

  // Collect all segment IDs for batch API call
  const segmentIds = useMemo(() => {
    // @ts-ignore
    return segments.map((segment) => segment.id);
  }, [segments]);

  // Fetch data for all segments in one API call
  const { data: allSegmentsData, isLoading: isSegmentDataLoading } = useDorisData({
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    metricType: DorisMetricTypeEnum.SegmentWithSalesSummary,
    fieldName: DorisGroupByFieldNameEnum.Segment,
    segmentIds: [segmentIds],
    enabled: segmentIds.length > 0,
    skipComparison: true
  });

  // Create a lookup map for segment data
  const segmentDataLookup = useMemo(() => {
    if (!allSegmentsData?.records) return {};

    return allSegmentsData.records.reduce((acc, record) => {
      acc[record.fieldKey] = record;
      return acc;
    }, {} as Record<string, SegmentData>);
  }, [allSegmentsData]);

  // Edit Business Unit Modal State
  const [businessUnitEditModalOpen, setBusinessUnitEditModalOpen] = useState(false);
  const [businessUnitSelected, setBusinessUnitSelected] = useState(null);

  // Delete Business Unit Modal State
  const [businessUnitDeleteModalOpen, setBusinessUnitDeleteModalOpen] = useState(false);
  const [businessUnitForDeletion, setBusinessUnitForDeletion] = useState(null);

  // Handle "Edit" button click
  const handleEditClick = (businessUnitToEdit: BusinessUnit) => {
    setBusinessUnitSelected(businessUnitToEdit);
    setBusinessUnitEditModalOpen(true);
  };

  // Handle closing of BU Edit modal
  const handleClose = () => {
    setBusinessUnitEditModalOpen(false);
    setBusinessUnitSelected(null);
  };

  // Handle "Delete" button click
  const handleDeleteClick = (businessUnitToDelete: BusinessUnit) => {
    setBusinessUnitDeleteModalOpen(true);
    setBusinessUnitForDeletion(businessUnitToDelete);
  };

  // Handle closing of BU Delete modal
  const handleDeleteModalClose = () => {
    setBusinessUnitDeleteModalOpen(false);
    setBusinessUnitForDeletion(null);
  };

  const segmentIdToSegmentMap = useMemo(() => {
    return segments.reduce(
      (acc, segment) => ({
        // @ts-ignore
        ...acc,
        // @ts-ignore
        [segment.id]: segment
      }),
      {} as { [key: string]: Segment }
    );
  }, [segments]);

  return (
    <Flex flexDirection="column" gap="lg">
      <Flex justifyContent="space-between" alignItems="center">
        {/* @ts-ignore */}
        <Link to={`/businessunit/${businessUnit.id}${searchParams}${additionalParams}`}>
          <Text variant="h4">{businessUnit.displayName || EMPTY_BUSINESS_UNIT_NAME}</Text>
        </Link>

        <Flex gap="md">
          <SlButton disabled={!businessUnit.canEdit} onClick={() => handleEditClick(businessUnit)}>
            Edit
          </SlButton>
          <SlButton
            disabled={!businessUnit.canEdit}
            onClick={() => handleDeleteClick(businessUnit)}
            buttonSx={{ backgroundColor: theme.colors.secondaryPorcelain }}
          >
            Delete
          </SlButton>
        </Flex>
      </Flex>
      <EntityTable
        appTableProps={{
          noDataPlaceholder: (
            <Box sx={{ margin: '12px 8px' }}>
              <NoDataPlaceholderWithChildrenSmall title="No segments?">
                <CreateNewSegmentLink text="Create a new segment" sx={{ fontWeight: 400, fontSize: '14px' }} />
              </NoDataPlaceholderWithChildrenSmall>
            </Box>
          )
        }}
        initialPageSize={10}
        supportViewMore
        shouldModifyColumnDefs={false}
        rowData={segments.map((segment) => {
          return {
            // @ts-ignore
            segment: segmentIdToSegmentMap[segment.id],
            // @ts-ignore
            segmentName: segmentIdToSegmentMap[segment.id].displayName,
            retailSales: 0,
            unitsSold: 0,
            retailPrice: 0,
            instockRate: 0,
            unitsOnHand: 0,
            lastUpdated: 'N/A'
          };
        })}
        columnDefs={[
          {
            headerName: 'Segment Name',
            field: 'segmentName',
            tableColumnProps: {
              style: {
                width: '480px'
              }
            },
            valueFormatter(_, row: { segment: Segment }) {
              return (
                // @ts-ignore
                <Link to={`/segment/${row.segment.id}${searchParams}${additionalParams}`}>
                  <Text variant="body2">{truncateWithEllipsis(50, row.segment.displayName, '...')}</Text>
                </Link>
              );
            }
          },
          {
            headerName: 'Retail Sales',
            field: 'retailSales',
            tableColumnProps: {
              style: {
                width: '200px'
              }
            },
            valueFormatter(_, row: { segment: Segment }) {
              return (
                <BusinessUnitMetricCell
                  fieldName="retailSales"
                  segmentId={row.segment.id}
                  segmentData={segmentDataLookup[row.segment.id]}
                  isLoading={isSegmentDataLoading}
                />
              );
            }
          },
          {
            headerName: 'Units Sold',
            field: 'unitsSold',
            tableColumnProps: {
              style: {
                width: '200px'
              }
            },
            valueFormatter(_, row: { segment: Segment }) {
              return (
                <BusinessUnitMetricCell
                  fieldName="unitsSold"
                  segmentId={row.segment.id}
                  segmentData={segmentDataLookup[row.segment.id]}
                  isLoading={isSegmentDataLoading}
                />
              );
            }
          },
          {
            headerName: 'Retail Price',
            field: 'retailPrice',
            tableColumnProps: {
              style: {
                width: '200px'
              }
            },
            valueFormatter(_, row: { segment: Segment }) {
              return (
                <BusinessUnitMetricCell
                  fieldName="retailPrice"
                  segmentId={row.segment.id}
                  segmentData={segmentDataLookup[row.segment.id]}
                  isLoading={isSegmentDataLoading}
                />
              );
            }
          },

          {
            headerName: 'Last Updated',
            field: 'lastUpdated',
            valueFormatter(_, row: { segment: Segment }) {
              // @ts-ignore
              const { userId } = row.segment;
              const { firstName, lastName } = row.segment.segment.owner;
              return (
                <UserTimestampCell
                  userId={userId}
                  date={row.segment.lastUpdatedTime}
                  firstName={firstName}
                  lastName={lastName}
                />
              );
            }
          }
        ]}
      />
      <div style={{ position: 'absolute' }}>
        {businessUnitEditModalOpen && businessUnitSelected ? (
          <BusinessUnitsCreationModal
            businessUnitToEdit={businessUnitSelected}
            open={businessUnitEditModalOpen}
            handleClose={handleClose}
          />
        ) : null}
      </div>
      <div style={{ position: 'absolute' }}>
        {businessUnitDeleteModalOpen && businessUnitForDeletion ? (
          <BusinessUnitsDeletionModal
            businessUnitToDelete={businessUnitForDeletion}
            open={businessUnitDeleteModalOpen}
            handleClose={handleDeleteModalClose}
          />
        ) : null}
      </div>
    </Flex>
  );
};

export default AtlasV2BusinessUnitTable;
